import React, { lazy, useEffect, useRef, useCallback } from "react";
import "sweetalert2/dist/sweetalert2.min.css";
import "./App.css";
import { ChevronDown, Check } from "lucide-react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { store } from "./store";

// Component Imports
import SideNav from "./components/SideNav/sidenav";

// Route Configuration
import { publicRoutes, privateRoutes, adminRoutes } from "./routes";

// Hooks and State
import { useJwtAuth } from "./hooks/useJwtAuth";
import { useAppDispatch, useAppSelector } from "./hooks/redux";
import {
  clearAuth,
  setAuth,
  setError,
  setLoading,
  stopImpersonation,
} from "./store/slices/authSlice";
import { useSelector } from "react-redux";
import {
  selectSubscriptionStatus,
  selectUserType,
} from "./store/slices/userSlice";
import { useCadenceTour } from "./hooks/useDashboardTour";
import { TourProvider, TourSection } from "./context/TourContext";
import { useUser } from "./hooks/useUser";
import { useDueReminders } from "./hooks/useDueReminders";
import ModalRenderer from "./components/modal/ModalRenderer";
import Button from "./components/ui/Button";
import { PageSkeleton } from "./components/ui/skeleton";

const SignUpPage = lazy(() => import("./pages/auth/SignUpPage"));
const LogInPage = lazy(() => import("./pages/auth/LogInPage"));

const useAuthValidation = () => {
  const { validateToken } = useJwtAuth();
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const validationRef = useRef(false);

  const performValidation = useCallback(async () => {
    if (!token || validationRef.current) return;

    validationRef.current = true;
    dispatch(setLoading(true));

    try {
      const tokenIsValid = await validateToken(token);

         if(!tokenIsValid && store.getState().auth.user?.impersonated) {
           store.dispatch(stopImpersonation());
          window.location.href = "/dashboard";
          return
         }

      if (!tokenIsValid) {
        throw new Error("Invalid token");
      } 

      dispatch(setAuth({ isAuthenticated: true }));
    } catch (error) {
      console.error("Auth validation error:", error);
      dispatch(
        setError(
          error instanceof Error ? error.message : "Authentication failed",
        ),
      );
      dispatch(clearAuth());
    } finally {
      dispatch(setLoading(false));
      validationRef.current = false;
    }
  }, [validateToken, dispatch, token]);

  return performValidation;
};

interface PrivateRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}
interface AdminRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  fallbackPath = "/auth/login",
}) => {
  const dispatch = useAppDispatch();
  const { token, loading, isAuthenticated, user } = useAppSelector(
    (state) => state.auth,
  );
  const performValidation = useAuthValidation();
  const allowRoles = ["admin", "basic", "dsp"];

  useEffect(() => {
    if (!token) {
      dispatch(clearAuth());
      return;
    }
    performValidation();
  }, [token, performValidation]); // Only run when token changes

  // The token is being validated. A page-shaped placeholder inside the shell,
  // not a blank content area.
  if (loading) {
    return <PageSkeleton />;
  }

  if (!isAuthenticated || (user?.role && !allowRoles.includes(user?.role))) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export const AdminRoute: React.FC<AdminRouteProps> = ({
  children,
  fallbackPath = "/dashboard",
}) => {
  const dispatch = useAppDispatch();
  const { token, loading, isAuthenticated, user } = useAppSelector(
    (state) => state.auth,
  );
  const performValidation = useAuthValidation();

  useEffect(() => {
    if (!token) {
      dispatch(clearAuth());
      return;
    }
    performValidation();
  }, [token, performValidation, dispatch]);

  // Same placeholder as PrivateRoute, rather than a full-screen spinner.
  if (loading) {
    return <PageSkeleton />;
  }

  // Restrict access to only admin users
  if (!isAuthenticated || user?.role !== "admin") {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export const SubscriptionGuard: React.FC<PrivateRouteProps> = ({
  children,
  fallbackPath = "/handle-subscription",
}) => {
  const userType = useSelector(selectUserType);
  const subscriptionStatus = useSelector(selectSubscriptionStatus);

  // Only redirect if user is subscription type AND status is past_due
  if (userType === "subscription" && subscriptionStatus === "past_due") {
    return <Navigate to={fallbackPath} replace />;
  }

  // Everyone else passes through
  return <>{children}</>;
};

// Helper to determine tour section from current path
const getTourSectionFromPath = (pathname: string): TourSection => {
  if (pathname.includes("/settings") && pathname.includes("dsps")) {
    return "dsps";
  }
  if (pathname.includes("/roster/artist-details")) {
    return "assets";
  }
  if (pathname.includes("/roster")) {
    return "roster";
  }
  if (pathname.includes("/pitch/create")) {
    return "createPitch";
  }
  return "dashboard";
};

const DueRemindersHost: React.FC = () => {
  const navigate = useNavigate();
  const dueReminders = useDueReminders();
  const [editingReminder, setEditingReminder] = React.useState<import("./types/reminder").DueReminder | null>(null);

  return (
    <>
      <ModalRenderer
        type="DUE_REMINDERS"
        isOpen={dueReminders.open}
        onClose={dueReminders.close}
        reminders={dueReminders.reminders}
        onDismiss={dueReminders.dismiss}
        onGoToPitch={(pitchId, id) => {
          dueReminders.close();
          navigate(`/pitch/submission/${pitchId}?from=dashboard`);
        }}
        onEdit={(reminder) => setEditingReminder(reminder)}
        onDelete={dueReminders.deleteReminder}
      />
      {editingReminder && (
        <ModalRenderer
          type="SET_REMINDER"
          isOpen={!!editingReminder}
          onClose={() => setEditingReminder(null)}
          pitchId={editingReminder.pitchId}
          artistName={editingReminder.pitch?.artistName}
          releaseTitle={editingReminder.pitch?.releaseTitle}
          existingReminder={editingReminder}
          onSuccess={() => {
            setEditingReminder(null);
            dueReminders.refetch();
          }}
        />
      )}
    </>
  );
};

const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { fetchProfile } = useUser();

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleStopImpersonation = () => {
    dispatch(stopImpersonation());
    navigate("/dashboard");
  };

  const tourSteps = [
    {
      id: 1,
      name:"dashboard"},
    {
      id: 2,
      name:"dsps"},
    {
      id: 3,
      name:"roster"},
    {
      id: 4,
      name:"assets"
    },
    {
      id: 5,
      name:"createPitch"
    }

  ]
  const location = useLocation();
  const currentSection = getTourSectionFromPath(
    location.pathname + location.search,
  );
  const { isMinimized, resumeTour, exitTour,getCurrentTourSection } = useCadenceTour(
    user?.role,
    currentSection,
  );
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const handleAccordionToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

 const currentTourSection = getCurrentTourSection();

 const currentIndex = tourSteps.findIndex(
  step => step.name === currentTourSection
);

const stepsWithStatus = tourSteps.map((step, index) => ({
  ...step,
  status: index < currentIndex ? 'completed' : index === currentIndex ? 'active' : 'pending'
}));

  return (
    <div className="app">
      <SideNav />
      <div className={`main-content ${user?.impersonated ? 'is-impersonating' : ''}`}>
        {/* Single line at a fixed height (App.css) so .is-impersonating can
            reserve exactly that much space; a long email truncates instead of
            wrapping. */}
        {user?.impersonated && (
          <div className="impersonation-banner flex items-center gap-3 px-4 text-sm font-medium text-white bg-primary-950">
            {/* Phone: small label stacked over the email and a short "Stop"
                button, so the email gets nearly the full width. */}
            <span
              className="flex flex-col min-w-0 leading-tight min-[769px]:block min-[769px]:truncate"
              title={user.email}
            >
              <span className="text-[11px] font-normal opacity-80 min-[769px]:hidden">Impersonating</span>
              <span className="hidden min-[769px]:inline">You are currently impersonating </span>
              <strong className="truncate">{user.email}</strong>
            </span>
            <Button
              size="sm"
              className="ml-auto shrink-0"
              aria-label="Stop Impersonating"
              onClick={handleStopImpersonation}
            >
              <span className="min-[769px]:hidden">Stop</span>
              <span className="hidden min-[769px]:inline">Stop Impersonating</span>
            </Button>
          </div>
        )}
        {isMinimized && user?.role === "basic" && (
          <div className={`transition-all duration-150 tour-floating-panel ${isCollapsed ? "h-[325px]" : "h-14"}`}>
            <div className="flex justify-between">
              <div className="tour-floating-panel-title">Cadenceio Tour</div>
          
                <ChevronDown
                  onClick={handleAccordionToggle}
                  className={`cursor-pointer tranis duration-150 ${isCollapsed ? "rotate-180" : ""} `}
                />
              
            </div>

            {isCollapsed ? (
              <div>
                <div className="tour-floating-panel-header">
             
                  {stepsWithStatus.map((step) => (
                    <div className="flex flex-row items-center gap-2 py-1 capitalize" key={step.id}>
                      <div className={`w-4 h-4 border-[2px] rounded-full flex items-center justify-center ${
                        step.status === 'completed'
                          ? 'bg-black border-black'
                          : step.status === 'active'
                          ? 'border-primary-500 bg-primary-100'
                          : 'border-neutral-400'
                      }`}>
                        {step.status === 'completed' && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className={`font-medium ${
                        step.status === 'active' ? 'text-primary-700' : 'text-neutral-700'
                      }`}>
                        {step.name}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    className="w-full tour-floating-resume-btn "
                    onClick={resumeTour}
                  >
                    Resume
                  </button>
                  <button className="w-full tour-floating-exit-btn" onClick={exitTour}>
                    Exit
                  </button>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);

  return (
    <Router>
      <TourProvider userId={user?.id} userRole={user?.role}>
        <DueRemindersHost />
        <Routes>
          {/* Public Routes */}
          {publicRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}

          {/* Root redirect */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <AuthenticatedLayout>
                  <Navigate to="/dashboard" replace />
                </AuthenticatedLayout>
              </PrivateRoute>
            }
          />

          {/* Private Routes */}
          {privateRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <PrivateRoute>
                  <SubscriptionGuard>
                    <AuthenticatedLayout>{route.element}</AuthenticatedLayout>
                  </SubscriptionGuard>
                </PrivateRoute>
              }
            />
          ))}

          {/* Admin Routes */}
          {adminRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <AdminRoute>
                  <AuthenticatedLayout>{route.element}</AuthenticatedLayout>
                </AdminRoute>
              }
            />
          ))}
        </Routes>
      </TourProvider>
    </Router>
  );
};

export default App;
